import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { UsersIcon, FilterIcon, SearchIcon, LayoutGridIcon, LayoutListIcon, BriefcaseIcon, PenToolIcon, MicIcon, ShoppingBagIcon, BookOpenIcon, WrenchIcon, HeartHandshakeIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { connectionProfiles } from './data';
import { VendorType } from './types';
const ConnectionsHub = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // URL Params
  const typeParam = searchParams.get('type') as VendorType || 'all';
  const queryParam = searchParams.get('q') || '';
  // State
  const [activeTab, setActiveTab] = useState<VendorType>(typeParam);
  const [searchQuery, setSearchQuery] = useState(queryParam);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter States
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedSubGenres, setSelectedSubGenres] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [selectedContentLevel, setSelectedContentLevel] = useState<string>('any');
  const [selectedWarnings, setSelectedWarnings] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('relevance');
  // Sync state with URL
  useEffect(() => {
    if (typeParam && typeParam !== activeTab) {
      setActiveTab(typeParam);
    }
    if (queryParam && queryParam !== searchQuery) {
      setSearchQuery(queryParam);
    }
  }, [typeParam, queryParam]);
  const updateUrl = (type: VendorType, query: string) => {
    const params: any = {};
    if (type && type !== 'all') params.type = type;
    if (query) params.q = query;
    setSearchParams(params);
  };
  const handleTabChange = (type: VendorType) => {
    setActiveTab(type);
    updateUrl(type, searchQuery);
  };
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    updateUrl(activeTab, query);
  };
  // Filter Logic
  const filteredProfiles = useMemo(() => {
    let result = [...connectionProfiles];
    // 1. Filter by Tab (Vendor Type)
    if (activeTab !== 'all') {
      result = result.filter(profile => profile.vendorType === activeTab);
    }
    // 2. Filter by Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.role.toLowerCase().includes(q) || p.bio?.toLowerCase().includes(q) || p.specialties.some(s => s.toLowerCase().includes(q)) || p.willWorkOn?.some(w => w.toLowerCase().includes(q)));
    }
    // 3. Filter by Genre (Broad)
    if (selectedGenres.length > 0) {
      result = result.filter(p => {
        // Check if profile works on ANY selected genre
        const worksOnGenre = p.willWorkOn?.some(g => selectedGenres.includes(g));
        // Also check if they specifically decline it
        const declinesGenre = p.wontWorkOn?.some(g => selectedGenres.includes(g));
        // Fallback to old genres array if willWorkOn is missing
        const hasLegacyGenre = p.genres?.some(g => selectedGenres.includes(g));
        return (worksOnGenre || hasLegacyGenre) && !declinesGenre;
      });
    }
    // 4. Filter by Sub-Genre (Specific)
    if (selectedSubGenres.length > 0) {
      result = result.filter(p => {
        const worksOnSub = p.willWorkOn?.some(g => selectedSubGenres.includes(g));
        const declinesSub = p.wontWorkOn?.some(g => selectedSubGenres.includes(g));
        return worksOnSub && !declinesSub;
      });
    }
    // 5. Filter by Content Level (Spice)
    if (selectedContentLevel !== 'any') {
      const requiredLevel = parseInt(selectedContentLevel);
      result = result.filter(p => {
        // If profile doesn't specify comfort, assume they are flexible or filter out?
        // Let's assume if undefined, they might not be comfortable with high spice
        const maxSpice = p.contentComfort?.maxSpice || 0;
        return maxSpice >= requiredLevel;
      });
    }
    // 6. Filter by Content Warnings (Exclusion Check)
    if (selectedWarnings.length > 0) {
      result = result.filter(p => {
        // If user selects "Violence", they want someone comfortable with Violence.
        // So we check if profile EXCLUDES it.
        const excludedByProfile = p.contentComfort?.excludeWarnings || [];
        // If profile excludes ANY of the selected warnings, filter them out
        const hasConflict = selectedWarnings.some(w => excludedByProfile.includes(w));
        return !hasConflict;
      });
    }
    // 7. Filter by Availability
    if (selectedAvailability.length > 0) {
      result = result.filter(p => {
        if (!p.availabilityLabel) return false;
        const label = p.availabilityLabel.toLowerCase();
        return selectedAvailability.some(a => label.includes(a.toLowerCase()));
      });
    }
    // 8. Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'rating-desc':
          return b.rating - a.rating;
        case 'reviews-desc':
          return b.reviewCount - a.reviewCount;
        default:
          return 0;
      }
    });
    return result;
  }, [activeTab, searchQuery, selectedGenres, selectedSubGenres, selectedAvailability, selectedContentLevel, selectedWarnings, sortBy]);
  // Filter Configuration
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'name-asc',
      label: 'Name (A-Z)'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'open',
      label: 'Open / Available'
    }, {
      value: 'limited',
      label: 'Limited Availability'
    }, {
      value: 'waitlist',
      label: 'Waitlist'
    }],
    value: selectedAvailability,
    onChange: val => setSelectedAvailability(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [{
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Mystery',
      label: 'Mystery'
    }, {
      value: 'Sci-Fi',
      label: 'Sci-Fi'
    }, {
      value: 'Thriller',
      label: 'Thriller'
    }, {
      value: 'Horror',
      label: 'Horror'
    }],
    value: selectedGenres,
    onChange: val => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'subgenre',
    title: 'Sub-Genre / Niche',
    type: 'checkbox',
    options: [{
      value: 'Dark Romance',
      label: 'Dark Romance'
    }, {
      value: 'Contemporary Romance',
      label: 'Contemporary Romance'
    }, {
      value: 'Paranormal Romance',
      label: 'Paranormal Romance'
    }, {
      value: 'Epic Fantasy',
      label: 'Epic Fantasy'
    }, {
      value: 'Urban Fantasy',
      label: 'Urban Fantasy'
    }, {
      value: 'Dark Fantasy',
      label: 'Dark Fantasy'
    }, {
      value: 'Romantasy',
      label: 'Romantasy'
    }, {
      value: 'Cozy Mystery',
      label: 'Cozy Mystery'
    }, {
      value: 'Space Opera',
      label: 'Space Opera'
    }, {
      value: 'Dystopian',
      label: 'Dystopian'
    }],
    value: selectedSubGenres,
    onChange: val => setSelectedSubGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'content-level',
    title: 'Content Comfort (Spice)',
    type: 'radio',
    options: [{
      value: 'any',
      label: 'Any Level'
    }, {
      value: '1',
      label: 'Spice 1+ (Mild)'
    }, {
      value: '3',
      label: 'Spice 3+ (Steamy)'
    }, {
      value: '5',
      label: 'Spice 5 (Explicit)'
    }],
    value: selectedContentLevel,
    onChange: setSelectedContentLevel
  }, {
    id: 'warnings',
    title: 'Content Comfort (Warnings)',
    type: 'checkbox',
    options: [{
      value: 'Violence',
      label: 'Violence'
    }, {
      value: 'Dark Themes',
      label: 'Dark Themes'
    }, {
      value: 'Dubcon',
      label: 'Dubcon / Noncon'
    }, {
      value: 'Explicit Content',
      label: 'Explicit Content'
    }],
    value: selectedWarnings,
    onChange: val => setSelectedWarnings(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const handleResetFilters = () => {
    setSelectedGenres([]);
    setSelectedSubGenres([]);
    setSelectedAvailability([]);
    setSelectedContentLevel('any');
    setSelectedWarnings([]);
    setSortBy('relevance');
    setSearchQuery('');
  };
  // Tabs Configuration
  const tabs: {
    id: VendorType;
    label: string;
    icon: any;
  }[] = [{
    id: 'all',
    label: 'All',
    icon: UsersIcon
  }, {
    id: 'authors',
    label: 'Authors',
    icon: PenToolIcon
  }, {
    id: 'narrators',
    label: 'Narrators',
    icon: MicIcon
  }, {
    id: 'vendors',
    label: 'Vendors',
    icon: ShoppingBagIcon
  }, {
    id: 'editors',
    label: 'Editors',
    icon: BookOpenIcon
  }, {
    id: 'production',
    label: 'Production',
    icon: WrenchIcon
  }, {
    id: 'support',
    label: 'Support',
    icon: HeartHandshakeIcon
  }, {
    id: 'creative',
    label: 'Creative',
    icon: BriefcaseIcon
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-8302" data-mp="2q5cy2t43lofh">
      {/* Header Section */}
      <div className={`pt-8 pb-6 px-4 sm:px-6 lg:px-8 ${dk.surface} border-b ${dk.border}`} data-id="element-8303" data-mp="2q3a4jz3qld60">
        <div className="max-w-7xl mx-auto" data-id="element-8304" data-mp="2yxeqq57xe0f7">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8" data-id="element-8305" data-mp="2yypwgakxli8y">
            <div data-id="element-8306" data-mp="3dvgxeoa9720o">
              <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-8307" data-mp="3duxfv8oimfn6">
                Connections Hub
              </h1>
              <p className={`text-lg ${dk.textSecondary}`} data-id="element-8308" data-mp="38trnjbyroue6">
                Discover and connect with professionals across the indie
                publishing industry.
              </p>
            </div>
            <div className="w-full md:w-96" data-id="element-8309" data-mp="3pwysrwn088vh">
              <SearchWithAutocomplete value={searchQuery} onChange={handleSearch} placeholder="Search by name, role, or specialty..." variant="pill" suggestions={[]} data-id="element-8310" data-mp="3pw6uuf5xpz21" />
            </div>
          </div>

          {/* Filter Pills (Tabs) */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0" data-id="element-8311" data-mp="7l28k7i6aqpd">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap border ${isActive ? `${rt.bgDark} text-white border-transparent shadow-md` : `${dk.surface} ${dk.textSecondary} ${dk.border} hover:${dk.surfaceMuted}`}`} data-id="element-8312" data-mp="2eaqm53yp9322">
                  <Icon className="h-4 w-4" data-id="element-8313" data-mp="21zpmz2ev32nx" />
                  {tab.label}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8314" data-mp="2und1q0tgaz2n">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-8315" data-mp="2poil95iyhmlp">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-8316" data-mp="2kow7muz7u4cd">
            <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-lg shadow-sm ${dk.card} ${dk.textPrimary}`} data-id="element-8317" data-mp="2knul9ucr1vfc">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-8318" data-mp="14bffw907xopy" />
              Filters & Sort
            </button>
          </div>

          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-8319" data-mp="2t4lfhtpnr3lt">
            <FilterSidebar title="Filters" sections={filterSections} onReset={handleResetFilters} searchable={false} data-id="element-8320" data-mp="2j5m443dictv4" />
          </FilterSidebarWrapper>

          {/* Results Area */}
          <div className="flex-1 min-w-0" data-id="element-8321" data-mp="3gu61yml0sizu">
            {/* Results Header & View Toggle */}
            <div className="flex items-center justify-between mb-6" data-id="element-8322" data-mp="31yq4s8p586z8">
              <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-8323" data-mp="36wrkcjnd1o5y">
                {filteredProfiles.length} result
                {filteredProfiles.length !== 1 && 's'} found
              </h2>

              <div className={`flex rounded-lg p-1 border ${dk.border} ${dk.surface}`} data-id="element-8324" data-mp="1vhmgvdfenlla">
                <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="Grid view" data-id="element-8325" data-mp="djge2f9dxfjh">
                  <LayoutGridIcon className="h-4 w-4" data-id="element-8326" data-mp="ij2mmbe9q7ly" />
                </button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? `${rt.bgDark} text-white shadow-sm` : `${dk.textMuted} hover:${dk.textPrimary}`}`} aria-label="List view" data-id="element-8327" data-mp="3qymfh9b11o0h">
                  <LayoutListIcon className="h-4 w-4" data-id="element-8328" data-mp="3v07mwf4sla3a" />
                </button>
              </div>
            </div>

            {/* Grid/List */}
            {filteredProfiles.length > 0 ? <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`} data-id="element-8329" data-mp="33a37kuvw3d8x">
                {filteredProfiles.map(profile => <ProfileCard key={profile.id} data={profile} viewMode={viewMode} data-id="element-8330" data-mp="1b78p90rvc3hz" />)}
              </div> : <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.border} ${dk.surfaceMuted}`} data-id="element-8331" data-mp="1kwigyfisvk5k">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-8332" data-mp="s95zgjrhy2j4">
                  <SearchIcon className={`h-8 w-8 ${dk.textMuted}`} data-id="element-8333" data-mp="xaln8jl26n57" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-8334" data-mp="12594bxdqgf9r">
                  No connections found
                </h3>
                <p className={`max-w-md mx-auto ${dk.textSecondary}`} data-id="element-8335" data-mp="2dlzd9o9jjl72">
                  Try adjusting your filters or search terms. You can also try
                  switching the vendor type tab.
                </p>
                <button onClick={handleResetFilters} className={`mt-6 font-medium ${rt.accentLink}`} data-id="element-8336" data-mp="3t6hwbzk8mith">
                  Clear all filters
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default ConnectionsHub;